import { faComment, faHeadset } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { EmptyState } from '../components/EmptyState';
import { IssueStatusChip } from '../components/IssueStatusChip';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { HistoryListSkeleton } from '../components/skeletons';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatStatementDate } from '../lib/formatStatementDate';
import * as portalIssueService from '../services/portalIssueService';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * Support requests (PD-13773) — the customer's own submitted issues with
 * case reference + status. The API scopes the list strictly to issues
 * THIS portal user created through the portal; other users' issues and
 * CRM-created issues never appear.
 */
export function SupportPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token, isAuthenticated } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);

	const { data, isLoading } = useQuery({
		queryKey: ['portal-issues'],
		queryFn: () => portalIssueService.listPortalIssues(token ?? ''),
		enabled: token != null,
		staleTime: 30_000,
	});

	const issues = data?.issues ?? [];

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Support" backPath="/account" />
			<AccountBreadcrumb current="Support" />
			<h1 className="hidden md:block text-lg md:text-xl font-bold text-default mb-4">
				{t('Support Requests')}
			</h1>

			{isLoading ? (
				<HistoryListSkeleton />
			) : issues.length === 0 ? (
				<EmptyState
					icon={faHeadset}
					title="No support requests yet"
					hint="Submit an issue from your dashboard and track it here."
				/>
			) : (
				<div className="flex flex-col gap-2">
					{issues.map((issue) => (
						<button
							key={issue.id}
							type="button"
							onClick={() => {
								void navigate(`/support/${issue.id}`);
							}}
							className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3 text-left hover:bg-brand-input transition-colors min-h-touch"
						>
							<div className="min-w-0">
								<div className="flex items-center gap-2 flex-wrap">
									<span className="text-sm font-medium text-default truncate">
										{t(issue.name)}
									</span>
									<IssueStatusChip status={issue.status} />
								</div>
								<div className="text-xs text-brand-muted mt-0.5">
									{t('#')}
									{issue.id}
									{issue.type != null && (
										<>
											{' · '}
											{t(issue.type)}
										</>
									)}
									{' · '}
									{formatStatementDate(issue.createdDate as unknown as string)}
								</div>
							</div>
							{issue.messageCount > 0 && (
								<div className="shrink-0 flex items-center gap-1 text-xs text-brand-muted">
									<FontAwesomeIcon icon={faComment} />
									{issue.messageCount}
								</div>
							)}
						</button>
					))}
				</div>
			)}
		</div>
	);
}
